import React, { useState, useEffect } from 'react'
import { ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { saveUserBet, getUserBets, updateUserBet, deleteUserBet } from '../services/userDataService'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKERS_BY_REGION as BOOKMAKERS, BOOKMAKER_LOGOS } from '../config/bookmakers'
import { MARKET_TYPE_MAPPING } from '../config/markets'
import { useAutoSettle } from './useAutoSettle'
import { isHorseBet, settleHorseBet } from '../components/horseBets'
import { liveProgress, liveLegs } from './settlement'
import LiveBetCard, { LiveMultiCard } from './LiveBetMeterMobile'
// The meters' CSS (bc-lm-* / bc-lmm-*) lives here. src/App.css is the stock
// Create React App leftover and is imported by nothing, so rules put there are
// silently dead — every page pulls this one in explicitly.
import '../styles/app.css'

// The date a bet is shown, sorted and filtered by: the game's start when the
// bet has one, otherwise placed_at. Tool bets save placed_at as the moment
// they were tracked, so reading it here would move each one to the day it was
// logged instead of the day its game is on. commence_time is naive UTC with no
// offset, stored with a space ("2026-10-13 08:00:00"), hence the T and the Z:
// Safari refuses the space form outright. Anything unreadable falls back to
// placed_at.
const betDate = (bet) => {
  const t = bet?.commence_time?.replace(' ', 'T')
  if (t) {
    const d = new Date(/Z$|[+-]\d\d:?\d\d$/.test(t) ? t : t + 'Z')
    if (!isNaN(d)) return d
  }
  return new Date(bet?.placed_at)
}

// The edit form's Date field, as it was always prefilled.
const placedDateInput = (bet) =>
  bet?.placed_at ? new Date(bet.placed_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]

// Every bet type the filter knows, in menu order. Counted rather than
// hardcoded so "all types selected" stays true when a new source ships; the
// checks were literal `=== 5`, which reads as "not all" the moment a sixth
// exists and leaves the pill claiming nothing is filtered while one is hidden.
//
// Deliberately its own copy rather than imported from TRACKER1.jsx: the two
// trackers label and colour these differently ("EV Single" vs "Single", amber
// vs blue for prop EV), so sharing the list would imply a shared vocabulary
// they do not actually have.
const MOBILE_TOOL_BET_TYPES = ['single', 'multi', 'dabble_pickem', 'prop_ev', 'prop_ev_multi']
const MOBILE_OTHER_BET_TYPES = ['odds_screen', 'odds_screen_multi', 'racing_odds']
const MOBILE_OTHER_LABELS = { odds_screen: 'Odds Screen', odds_screen_multi: 'Odds Multi', racing_odds: 'Racing Odds' }

// Same rule as TRACKER1: checkboxes within Other, so sports and racing bets can be viewed apart.
const toggleOtherType = (filter, key, tools) => {
  if (!MOBILE_OTHER_BET_TYPES.some(t => filter.has(t))) return new Set([key])
  const next = new Set(filter)
  next.has(key) ? next.delete(key) : next.add(key)
  return next.size ? next : new Set(tools)
}
const MOBILE_BET_TYPES = [...MOBILE_TOOL_BET_TYPES, ...MOBILE_OTHER_BET_TYPES]

/** True when the filter is showing the odds-screen set rather than the tools. */
const isOtherMode = (filter) => MOBILE_OTHER_BET_TYPES.some(t => filter.has(t))

const MONO = "ui-monospace, 'SF Mono', Menlo, Consolas, 'Liberation Mono', monospace"
const SANS = "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif"

const s = {
  container: { width: '100%', margin: 0, padding: 0, backgroundColor: '#010714', minHeight: '100vh', boxSizing: 'border-box' },
  card: { backgroundColor: '#010714', borderRadius: '0px', border: 'none', borderBottom: '1px solid #1b2438', padding: '16px', marginBottom: 0 },
  row: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  title: { fontSize: '24px', fontWeight: '600', margin: 0, color: '#f1f5f9' },
  subtitle: { fontSize: '18px', fontWeight: '700', margin: 0, color: '#f1f5f9' },
  label: { fontWeight: '600', color: '#64748b', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  // Mobile-optimized stat cards
  statCard: { background: 'transparent', borderRadius: '8px', padding: '10px 12px', border: '1px solid #1b2538', minWidth: '96px', maxWidth: '150px', flexShrink: 0 },
  statValue: { fontSize: '19px', fontWeight: '500', marginTop: '6px', lineHeight: '1', fontFamily: MONO, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' },
  statLabel: { fontSize: '10px', color: '#8a8f96', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: SANS, lineHeight: '1.2' },
  statMarquee: { display: 'flex', width: 'max-content', animation: 'trackerStatScroll 18s linear infinite' },
  btn: { padding: '10px 20px', fontSize: '13px', fontWeight: '500', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif', background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', color: '#f1f5f9', border: '1px solid #3b82f6', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s ease', letterSpacing: '0.01em', boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)' },
  btnSecondary: { padding: '8px 12px', fontSize: '12px', fontWeight: '500', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif', background: '#0d1628', color: '#cbd5e1', border: '1px solid #1f2b42', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s ease', letterSpacing: '0.01em' },
  btnActive: { padding: '8px 12px', fontSize: '12px', fontWeight: '500', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif', background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', color: '#f1f5f9', border: '1px solid #3b82f6', borderRadius: '6px', cursor: 'pointer', transition: 'all 0.15s ease', letterSpacing: '0.01em', boxShadow: '0 2px 6px rgba(37, 99, 235, 0.25)' },
  // Form fields and modal: the refined modal surfaces, matching desktop
  // (TRACKER1.jsx) and the tool pages' modals.
  input: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '11px 13px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  select: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '11px 13px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' },
  pill: { borderRadius: '12px', border: '1px solid #1b2538', background: '#0b1220', color: '#cbd5e1', fontSize: '13px', fontWeight: '400', padding: '8px 12px', cursor: 'pointer', transition: 'all 0.15s ease' },
  pillActive: { borderRadius: '12px', border: '1px solid #3b82f6', background: 'rgba(59,130,246,0.15)', color: '#f1f5f9', fontSize: '13px', fontWeight: '600', padding: '8px 12px', cursor: 'pointer', transition: 'all 0.15s ease' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableWrapper: { maxHeight: '400px', overflowY: 'auto', border: '1px solid #334155', borderRadius: '8px', backgroundColor: '#1e293b' },
  th: { padding: '16px', textAlign: 'left', fontWeight: '600', color: '#f1f5f9', backgroundColor: '#334155', borderBottom: '1px solid #1b2538' },
  td: { padding: '16px', borderBottom: '1px solid #1b2538', color: '#f1f5f9' },
  badge: { padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' },
  badgeGreen: { backgroundColor: '#065f46', color: '#34d399', border: '1px solid #047857' },
  badgeRed: { backgroundColor: '#7f1d1d', color: '#fca5a5', border: '1px solid #b91c1c' },
  badgeBlue: { backgroundColor: '#1e3a8a', color: '#60a5fa', border: '1px solid #3730a3' },
  badgeGray: { backgroundColor: '#374151', color: '#d1d5db', border: '1px solid #4b5563' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(1, 4, 12, 0.66)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#080e1b', borderRadius: '16px', padding: '16px', border: '1px solid #1b2438', boxShadow: '0 24px 72px rgba(0, 0, 0, 0.65)', maxWidth: '440px', width: '95%', maxHeight: '88vh', overflowY: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', paddingBottom: '12px', borderBottom: '1px solid #1b2438' },
  closeBtn: { backgroundColor: 'transparent', border: 'none', color: '#5f6d82', fontSize: '24px', cursor: 'pointer', padding: '4px' },
  dropdownButton: { border: '1px solid #1f2b42', borderRadius: '8px', padding: '11px 13px', backgroundColor: '#0d1628', color: '#e4e9f0', fontSize: '14px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  dropdownList: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, backgroundColor: '#0d1628', border: '1px solid #1f2b42', boxShadow: '0 12px 32px rgba(0, 0, 0, 0.55)', borderRadius: '10px', maxHeight: '200px', overflowY: 'auto', marginTop: '4px' },
  dropdownItem: { padding: '12px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #1b2438' },
  deleteBtn: { backgroundColor: '#374151', color: '#9ca3af', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' },
  filterBtn: { border: '1px solid #1b2538', borderRadius: '8px', padding: '7px 10px', background: 'transparent', color: '#cbd5e1', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif', letterSpacing: '0.01em', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px', whiteSpace: 'nowrap', transition: 'all 0.15s ease' },
  chipLabel: { flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    backgroundColor: '#0a1020',
    border: '1px solid #1b2538',
    borderRadius: '10px',
    padding: '16px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
    minWidth: '150px',
    maxWidth: '90vw',
    width: 'max-content'
  },
  checkbox: { display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#e2e8f0', padding: '7px 10px', borderRadius: '8px', border: '1px solid #1b2538', backgroundColor: 'transparent', transition: 'all 0.2s ease', fontSize: '13px', fontWeight: '500' },
  inputGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: '12px' },

  // Mobile bet card styles
  betCard: {
    backgroundColor: 'transparent',
    borderRadius: '10px',
    border: '1px solid #1b2538',
    padding: '14px',
    marginBottom: '10px'
  },
  betCardHeader: {
    marginBottom: '10px',
    paddingBottom: '10px',
    borderBottom: '1px solid #1b2538'
  },
  betCardMatch: {
    fontSize: '14.5px',
    fontWeight: '600',
    color: '#f1f5f9',
    margin: 0,
    lineHeight: '1.25'
  },
  betCardRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    borderBottom: '1px solid rgba(255,255,255,0.04)'
  },
  betCardRowLast: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0'
  },
  cardLabel: {
    fontSize: '10px',
    color: '#8a8f96',
    fontWeight: '600',
    fontFamily: SANS,
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },
  cardValue: {
    fontSize: '13px',
    color: '#e2e8f0',
    fontWeight: '500',
    fontVariantNumeric: 'tabular-nums'
  },
  cardValueHighlight: {
    fontSize: '13px',
    fontWeight: '500',
    fontFamily: MONO,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.01em'
  },
  actionButtons: {
    display: 'flex',
    gap: '6px',
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #1b2538'
  },
  // Mobile stats grid
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    marginBottom: '16px'
  }
}


const SPORT_DISPLAY_NAMES = {
  "NBA": "NBA",
  "WNBA": "WNBA",
  "NBL": "NBL",
  "AFL": "AFL",
  "AFLW": "AFLW",
  "NRL": "NRL",
  "NRLW": "NRLW",
  "RLWC": "RL World Cup",
  "MLS": "MLS",
  "BUNDESLIGA": "Bundesliga",
  "UCL": "UCL",
  "EPL": "EPL",
  "LA_LIGA": "La Liga",
  "SERIE_A": "Serie A",
  "LIGUE_1": "Ligue 1",
  "A_LEAGUE": "A-League",
  "WORLD_CUP": "World Cup",
  "MLB": "MLB",
  "NFL": "NFL",
  "NHL": "NHL",
  "UFC": "UFC",
  "ATP_AUSTRALIAN_OPEN": "ATP Australian Open",
  "ATP_FRENCH_OPEN": "ATP French Open",
  "ATP_WIMBLEDON": "ATP Wimbledon",
  "ATP_US_OPEN": "ATP US Open",
  "ATP_INDIAN_WELLS": "ATP Indian Wells",
  "ATP_MIAMI": "ATP Miami",
  "ATP_MONTE_CARLO": "ATP Monte Carlo",
  "ATP_MADRID": "ATP Madrid",
  "ATP_ROME": "ATP Rome",
  "ATP_CANADA": "ATP Canada",
  "ATP_CINCINNATI": "ATP Cincinnati",
  "ATP_SHANGHAI": "ATP Shanghai",
  "ATP_PARIS": "ATP Paris",
  "ATP_FINALS": "ATP Finals",
  // The whole Challenger tier behind one id, so tracked Challenger bets all
  // carry sport "ATP_CHALLENGER" and group under this one label rather than
  // spawning ~190 competitions a year.
  "ATP_CHALLENGER": "ATP Challenger",
  // Retired ids, display only — bets tracked before the Aug 2026 rename.
  "MENS_AO": "Mens Australian Open",
  "MENS_RG": "Mens Roland Garros",
  "MENS_WIMBLEDON": "Mens Wimbledon",
  "MENS_USO": "Mens US Open",
  "Horse": "Horse Racing",
  "Harness": "Harness Racing",
  "Greyhounds": "Greyhounds"
}

const SPORT_HIERARCHY = {
  "Rugby League": {
    leagues: ["NRL", "NRLW", "RLWC"]
  },
  "Aussie Rules": {
    leagues: ["AFL", "AFLW"]
  },
  "Soccer": {
    leagues: ["MLS", "BUNDESLIGA", "UCL", "EPL", "LA_LIGA", "SERIE_A", "LIGUE_1", "A_LEAGUE", "WORLD_CUP"]
  },
  "Basketball": {
    leagues: ["NBA", "WNBA", "NBL"]
  },
  "Baseball": {
    leagues: ["MLB"]
  },
  "American Football": {
    leagues: ["NFL"]
  },
  "Hockey": {
    leagues: ["NHL"]
  },
  "MMA": {
    leagues: ["UFC"]
  },
  "Tennis": {
    leagues: ["ATP_AUSTRALIAN_OPEN", "ATP_FRENCH_OPEN", "ATP_WIMBLEDON", "ATP_US_OPEN", "ATP_INDIAN_WELLS", "ATP_MIAMI", "ATP_MONTE_CARLO", "ATP_MADRID", "ATP_ROME", "ATP_CANADA", "ATP_CINCINNATI", "ATP_SHANGHAI", "ATP_PARIS", "ATP_FINALS", "ATP_CHALLENGER"]
  },
  "Racing": {
    leagues: ["Horse", "Harness", "Greyhounds"]
  }
}

const formatMarketType = (marketType, playerName = '') => {
  // First check if we have a predefined display format for this market type
  if (MARKET_TYPE_MAPPING[marketType]) {
    return MARKET_TYPE_MAPPING[marketType]
  }

  // Fallback for player props without defined formats
  if (playerName && marketType.includes('player_')) {
    const statType = marketType.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    return `Player Prop - ${statType}`
  }

  // Final fallback - format the raw market type
  return marketType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

// Closed-filter-chip icons (Feather-style, matching the Expo app's chip glyphs).
// color defaults to the app's inactive icon colour (#64748b).
const ChipIcon = ({ name, color = '#64748b' }) => {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: color, strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', style: { flexShrink: 0 } }
  switch (name) {
    case 'time': // clock
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>)
    case 'sport': // basketball
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6c3 3 3.8 9.6 2 12.8" /><path d="M18.4 5.6c-3 3-3.8 9.6-2 12.8" /><line x1="3" y1="12" x2="21" y2="12" /></svg>)
    case 'book': // book
      return (<svg {...common}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>)
    case 'tags': // pricetags
      return (<svg {...common}><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>)
    default:
      return null
  }
}

const ChipChevron = ({ open, color = '#64748b' }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

// Checkmark shown on a selected option row (matches app OptionRow tick, #60a5fa).
const RowTick = ({ show }) => show ? (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginLeft: 'auto' }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
) : null

export default function BetTracker2({ isVisible = true }) {
  const { user, oddsFormat } = useAuth()
  // Per-tab bet state: setters are kept in sync but the values are read via allEvBets/allArbBets
  // etc. for display/stats. Unread pending a refactor to drop these in favour of the "all" lists.
  /* eslint-disable no-unused-vars */
  const [evBets, setEvBets] = useState([])
  const [arbBets, setArbBets] = useState([])
  const [promoBets, setPromoBets] = useState([])
  const [middleBets, setMiddleBets] = useState([])
  /* eslint-enable no-unused-vars */

  // All bets for stats calculations (unlimited)
  const [allEvBets, setAllEvBets] = useState([])
  const [allArbBets, setAllArbBets] = useState([])
  const [allPromoBets, setAllPromoBets] = useState([])
  const [allMiddleBets, setAllMiddleBets] = useState([])
  const [activeTab, setActiveTab] = useState('EV')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(() => Number(localStorage.getItem('tracker_items_per_page')) || 50)
  const [statusFilter, setStatusFilter] = useState('all')
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true)
  const [timeFilter, setTimeFilter] = useState('all')
  const [customDateStart, setCustomDateStart] = useState('')
  const [customDateEnd, setCustomDateEnd] = useState('')
  const [calendarDate, setCalendarDate] = useState(new Date())
  const [sportFilter, setSportFilter] = useState('all')
  const [bookmakerFilter, setBookmakerFilter] = useState('all')
  const [betTypeFilter, setBetTypeFilter] = useState(new Set(MOBILE_TOOL_BET_TYPES))
  const [showAddForm, setShowAddForm] = useState(false)
  const [showBookmakerDropdown, setShowBookmakerDropdown] = useState(false)
  const [showBookmaker2Dropdown, setShowBookmaker2Dropdown] = useState(false)
  const [showTimeDropdown, setShowTimeDropdown] = useState(false)
  const [showSportDropdown, setShowSportDropdown] = useState(false)
  const [showBookmakerFilterDropdown, setShowBookmakerFilterDropdown] = useState(false)
  const [showBetTypeDropdown, setShowBetTypeDropdown] = useState(false)
  const [editingBetId, setEditingBetId] = useState(null)
  // The edit form only carries user-editable fields, but updateUserBet replaces the
  // whole bet_data blob. Keep the original so everything the form doesn't know about
  // (commence_time, the settlement fields) survives an edit instead of being dropped.
  const [editingBetOriginal, setEditingBetOriginal] = useState(null)
  const [newBet, setNewBet] = useState({ type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO', sport: 'NBA', match: '', selection: '', bookmaker: '', bookmaker2: '', market_type: '', odds: '', stake: '', ev_percentage: '', profit: '', qualifying_loss: '', date: new Date().toISOString().split('T')[0], promo_type: 'Mug Bet', bonus_received: false, bonus_amount: '', free_bonus: false, bet_type_tag: 'single' })
  const [expandedSports, setExpandedSports] = useState(new Set())


  useEffect(() => {
    const loadUserBets = async () => {
      
      if (!user?.id) {
        setLoading(false)
        return
      }

      try {
        const result = await getUserBets(user.id, null) // null = get all bets for tracker
        
        const { data: userBets, error } = result
        
        if (error) {
          console.error('Error loading user bets:', error)
          setEvBets([])
          setArbBets([])
          setPromoBets([])
          setMiddleBets([])
          setAllEvBets([])
          setAllArbBets([])
          setAllPromoBets([])
          setAllMiddleBets([])
        } else {
          
          // Separate EV, ARB, PROMO, and MIDDLE bets - ALL DATA for stats
          const allEvBets = userBets.filter(bet => {
            return bet.bet_data.type === 'EV'
          }).map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allArbBets = userBets.filter(bet => bet.bet_data.type === 'ARB').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allPromoBets = userBets.filter(bet => bet.bet_data.type === 'PROMO').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          const allMiddleBets = userBets.filter(bet => bet.bet_data.type === 'MIDDLE').map(bet => ({
            id: bet.id,
            ...bet.bet_data
          }))

          // Cap recent bets at 50 for table display (but keep all for stats)
          const evBetData = allEvBets.slice(0, 50)
          const arbBetData = allArbBets.slice(0, 50)
          const promoBetData = allPromoBets.slice(0, 50)
          const middleBetData = allMiddleBets.slice(0, 50)


          // Store recent bets for table display
          setEvBets(evBetData)
          setArbBets(arbBetData)
          setPromoBets(promoBetData)
          setMiddleBets(middleBetData)

          // Store all bets for stats calculations
          setAllEvBets(allEvBets)
          setAllArbBets(allArbBets)
          setAllPromoBets(allPromoBets)
          setAllMiddleBets(allMiddleBets)
          
        }
      } catch (error) {
        console.error('Catch block error:', error)
        setEvBets([])
        setArbBets([])
        setPromoBets([])
        setMiddleBets([])
        setAllEvBets([])
        setAllArbBets([])
        setAllPromoBets([])
        setAllMiddleBets([])
      } finally {
        setLoading(false)
      }
    }

    loadUserBets()
  }, [user?.id])

  // Update bet type when active tab changes
  useEffect(() => {
    if (!editingBetId) { // Only update if not editing an existing bet
      setNewBet(prev => ({ 
        ...prev, 
        type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO' 
      }))
    }
  }, [activeTab, editingBetId])

  // Database saving happens when bets are added/updated, not on every state change

  // Helper functions for hierarchical sports filter
  const toggleSportCategory = (sportName) => {
    setExpandedSports(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sportName)) {
        newSet.delete(sportName)
      } else {
        newSet.add(sportName)
      }
      return newSet
    })
  }

  const toggleParentSport = (sportName) => {
    const sportData = SPORT_HIERARCHY[sportName]
    if (!sportData) return

    if (isSportFullySelected(sportName)) {
      // Deselect all leagues in this category
      setSportFilter('all')
    } else {
      // For single-select filter, just select the first league in the category
      // Or we could show all leagues - but tracker uses single select not multi-select
      // Let's just set to 'all' to avoid confusion
      setSportFilter('all')
    }
  }

  const isSportFullySelected = (sportName) => {
    const sportData = SPORT_HIERARCHY[sportName]
    if (!sportData) return false
    // Since sportFilter is single-select, check if it matches any league in the category
    return sportData.leagues.includes(sportFilter)
  }

  const isSportPartiallySelected = (sportName) => {
    // Not applicable for single-select, always return false
    return false
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.filter-dropdown') && !event.target.closest('.bookmaker-dropdown')) {
        setShowTimeDropdown(false)
        setShowSportDropdown(false)
        setShowBookmakerFilterDropdown(false)
        setShowBookmakerDropdown(false)
        setShowBookmaker2Dropdown(false)
        setShowBetTypeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Helper function to close all dropdowns except the specified one
  const toggleDropdown = (dropdownName) => {
    const wasOpen = {
      time:            showTimeDropdown,
      sport:           showSportDropdown,
      bookmakerFilter: showBookmakerFilterDropdown,
      bookmaker:       showBookmakerDropdown,
      bookmaker2:      showBookmaker2Dropdown,
      betType:         showBetTypeDropdown,
    }[dropdownName]

    setShowTimeDropdown(false)
    setShowSportDropdown(false)
    setShowBookmakerFilterDropdown(false)
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)
    setShowBetTypeDropdown(false)

    if (!wasOpen) {
      switch(dropdownName) {
        case 'time':            setShowTimeDropdown(true);            break
        case 'sport':           setShowSportDropdown(true);           break
        case 'bookmakerFilter': setShowBookmakerFilterDropdown(true); break
        case 'bookmaker':       setShowBookmakerDropdown(true);       break
        case 'bookmaker2':      setShowBookmaker2Dropdown(true);      break
        case 'betType':         setShowBetTypeDropdown(true);         break
        default: break
      }
    }
  }

  const getFilteredBets = () => {
    let bets = activeTab === 'EV' ? allEvBets : activeTab === 'ARB' ? allArbBets : activeTab === 'MIDDLE' ? allMiddleBets : allPromoBets

    // Time filter
    if (timeFilter !== 'all') {
      if (timeFilter === 'custom') {
        if (customDateStart) bets = bets.filter(bet => betDate(bet) >= new Date(customDateStart))
        if (customDateEnd) { const end = new Date(customDateEnd); end.setHours(23,59,59,999); bets = bets.filter(bet => betDate(bet) <= end) }
      } else {
        const now = new Date()
        const cutoffDate = new Date()
        if (timeFilter === '7days') cutoffDate.setDate(now.getDate() - 7)
        if (timeFilter === '1month') cutoffDate.setDate(now.getDate() - 30)
        bets = bets.filter(bet => betDate(bet) >= cutoffDate)
      }
    }

    // Sport filter
    if (sportFilter !== 'all') {
      bets = bets.filter(bet => bet.sport === sportFilter)
    }

    // Bookmaker filter
    if (bookmakerFilter !== 'all') {
      bets = bets.filter(bet => bet.bookmaker === bookmakerFilter || bet.bookmaker2 === bookmakerFilter)
    }

    // Bet type filter (EV only)
    // No "< all types" shortcut. That guard was a hardcoded 5, so the moment a
    // SIXTH type existed the default selection (the five tool types) stopped
    // counting as a partial filter and nothing was filtered at all — which is
    // how odds-screen bets ended up in the table, the stats and the EV chart
    // despite being deselected. Filtering when everything is selected is a
    // no-op anyway, so the shortcut only ever bought a bug.
    if (activeTab === 'EV' && betTypeFilter.size > 0) {
      bets = bets.filter(bet => betTypeFilter.has(bet.bet_type_tag || 'single'))
    }

    // Status filter
    if (statusFilter !== 'all') {
      bets = bets.filter(bet => bet.status === statusFilter)
    }

    return bets
  }

  const getAllFilteredBets = () => {
    // Use ALL bets for stats calculations (not capped at 50)
    let bets = activeTab === 'EV' ? allEvBets : activeTab === 'ARB' ? allArbBets : activeTab === 'MIDDLE' ? allMiddleBets : allPromoBets

    // Apply same filters as table but on complete dataset
    if (timeFilter !== 'all') {
      if (timeFilter === 'custom') {
        if (customDateStart) bets = bets.filter(bet => betDate(bet) >= new Date(customDateStart))
        if (customDateEnd) { const end = new Date(customDateEnd); end.setHours(23,59,59,999); bets = bets.filter(bet => betDate(bet) <= end) }
      } else {
        const now = new Date()
        const cutoffDate = new Date()
        if (timeFilter === '7days') cutoffDate.setDate(now.getDate() - 7)
        if (timeFilter === '1month') cutoffDate.setDate(now.getDate() - 30)
        bets = bets.filter(bet => betDate(bet) >= cutoffDate)
      }
    }

    if (sportFilter !== 'all') {
      bets = bets.filter(bet => bet.sport === sportFilter)
    }

    if (bookmakerFilter !== 'all') {
      bets = bets.filter(bet => bet.bookmaker === bookmakerFilter || bet.bookmaker2 === bookmakerFilter)
    }

    // Bet type filter (EV only)
    // No "< all types" shortcut. That guard was a hardcoded 5, so the moment a
    // SIXTH type existed the default selection (the five tool types) stopped
    // counting as a partial filter and nothing was filtered at all — which is
    // how odds-screen bets ended up in the table, the stats and the EV chart
    // despite being deselected. Filtering when everything is selected is a
    // no-op anyway, so the shortcut only ever bought a bug.
    if (activeTab === 'EV' && betTypeFilter.size > 0) {
      bets = bets.filter(bet => betTypeFilter.has(bet.bet_type_tag || 'single'))
    }

    return bets
  }

  const currentBets = getFilteredBets().sort((a, b) => betDate(b) - betDate(a))
  const allCurrentBets = getAllFilteredBets().sort((a, b) => betDate(b) - betDate(a))

  const totalPages = Math.ceil(currentBets.length / itemsPerPage)
  const paginatedBets = currentBets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  useEffect(() => { setCurrentPage(1) }, [activeTab, timeFilter, sportFilter, bookmakerFilter, betTypeFilter, statusFilter])

  // Auto-settle pending bets against the results feed. Only EV bets can be
  // settled this way (ARB/MIDDLE/PROMO carry a known profit at entry), so only
  // those lists need patching when a result comes back. Makes no requests when
  // nothing is pending. See useAutoSettle.js.
  const autoSettleBets = React.useMemo(
    () => [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets],
    [allEvBets, allArbBets, allPromoBets, allMiddleBets]
  )

  // Which multis have had their legs opened. Collapsed is the default, so this
  // holds the exceptions rather than the state of every multi, and a bet that
  // leaves the list simply stops being in it. Ids, not indexes, so paging or a
  // re-sort cannot open somebody else's legs.
  const [openMultis, setOpenMultis] = useState(() => new Set())
  const toggleMulti = React.useCallback((id) => {
    setOpenMultis(prev => {
      const next = new Set(prev)
      if (!next.delete(id)) next.add(id)
      return next
    })
  }, [])

  // liveGames maps bet id -> the fixture it matched, used to show where an
  // in-progress bet currently stands. Display only; settlement is the hook's job.
  const { liveGames } = useAutoSettle(autoSettleBets, React.useCallback((applied) => {
    const byId = new Map(applied.map(a => [a.id, a]))
    const patch = (list) => list.map(b => (byId.has(b.id) ? { ...b, ...byId.get(b.id) } : b))
    setEvBets(patch)
    setAllEvBets(patch)
  }, []))

  const recentBetsRef = React.useRef(null)
  const goToPage = (page) => { setCurrentPage(page); recentBetsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }

  const stats = React.useMemo(() => {
    // Use ALL bets for stats calculations (complete dataset)
    const settledBets = allCurrentBets.filter(bet => bet.status !== 'pending')
    const totalStake = allCurrentBets.reduce((sum, bet) => sum + bet.stake, 0)
    const totalProfit = settledBets.reduce((sum, bet) => sum + (bet.actual_profit || 0), 0)
    const theoreticalEV = allCurrentBets.reduce((sum, bet) => {
      if (bet.ev_percentage && activeTab === 'EV') {
        return sum + ((bet.ev_percentage / 100) * bet.stake)
      }
      return sum
    }, 0)
    const roi = totalStake > 0 ? (totalProfit / totalStake) * 100 : 0
    const winRate = settledBets.length > 0 ? (settledBets.filter(bet => bet.status === 'won').length / settledBets.length) * 100 : 0
    
    // PROMO specific stats - use ALL promo bets for accurate calculations
    let promoStats = {}
    if (activeTab === 'PROMO') {
      const bonusReceived = allPromoBets.filter(bet => bet.bonus_received).reduce((sum, bet) => sum + (bet.bonus_amount || 0), 0)
      const bonusCleared = allPromoBets.filter(bet => bet.promo_type === 'Bonus Turnover' && !bet.free_bonus).reduce((sum, bet) => sum + bet.stake, 0)
      const pendingBonusTurnover = bonusReceived - bonusCleared

      // Bonus retention: profit vs stake from all bonus turnover bets
      const bonusTurnoverBets = allPromoBets.filter(bet => bet.promo_type === 'Bonus Turnover' && bet.status !== 'pending')
      const bonusTurnoverStake = bonusTurnoverBets.reduce((sum, bet) => sum + (bet.stake || 0), 0)
      const bonusTurnoverProfit = bonusTurnoverBets.reduce((sum, bet) => sum + (bet.actual_profit || 0), 0)
      const bonusRetention = bonusTurnoverStake > 0 ? (bonusTurnoverProfit / bonusTurnoverStake) * 100 : 0

      const mugBets = allPromoBets.filter(bet => bet.promo_type === 'Mug Bet').length
      const promoBetsCount = allPromoBets.filter(bet => bet.promo_type === 'Promo Bet').length
      const promoMugRatio = mugBets > 0 ? (promoBetsCount / mugBets).toFixed(1) : '0'
      const avgProfitPerBet = allCurrentBets.length > 0 ? totalProfit / allCurrentBets.length : 0

      promoStats = { pendingBonusTurnover, bonusRetention, promoMugRatio, avgProfitPerBet }
    }
    
    return { totalProfit, totalStake, roi, winRate, totalBets: allCurrentBets.length, theoreticalEV, ...promoStats }
  }, [allCurrentBets, activeTab, allPromoBets])

  const chartData = React.useMemo(() => {
    // Use ALL bets for complete profit chart history
    const sortedBets = [...allCurrentBets].filter(bet => bet.status !== 'pending').sort((a, b) => new Date(a.settled_at) - new Date(b.settled_at))
    let runningProfit = 0
    let runningTheoreticalEV = 0
    return sortedBets.map((bet, index) => {
      runningProfit += bet.actual_profit || 0
      if (bet.ev_percentage && activeTab === 'EV') {
        runningTheoreticalEV += (bet.ev_percentage / 100) * bet.stake
      }
      return { 
        bet: index + 1, 
        profit: runningProfit, 
        theoreticalEV: activeTab === 'EV' ? runningTheoreticalEV : null,
        date: new Date(bet.settled_at).toLocaleDateString() 
      }
    })
  }, [allCurrentBets, activeTab])

  const calendarDailyProfits = React.useMemo(() => {
    const map = {}
    allCurrentBets.filter(b => b.status !== 'pending').forEach(bet => {
      const d = new Date(bet.settled_at || bet.placed_at)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
      map[key] = (map[key] || 0) + (bet.actual_profit || 0)
    })
    return map
  }, [allCurrentBets])

  const addBet = async () => {
    
    if (!newBet.match || !newBet.stake) {
      return
    }
    if (newBet.type === 'EV' && (!newBet.odds || !newBet.selection || !newBet.bookmaker)) {
      return
    }
    if (newBet.type === 'ARB' && (!newBet.bookmaker || !newBet.bookmaker2)) {
      return
    }
    if (newBet.type === 'PROMO' && !newBet.bookmaker) {
      return
    }
    
    const bet = { 
      ...newBet, 
      odds: newBet.odds ? parseFloat(newBet.odds) : null, 
      stake: parseFloat(newBet.stake), 
      ev_percentage: newBet.ev_percentage ? parseFloat(newBet.ev_percentage) : null,
      bonus_amount: newBet.bonus_amount ? parseFloat(newBet.bonus_amount) : null,
      status: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? (parseFloat(newBet.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? parseFloat(newBet.profit) : null,
      settled_at: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? new Date(newBet.date).toISOString() : null, 
      placed_at: new Date(newBet.date).toISOString() 
    }
    
    
    // Save to database
    if (user?.id) {
      try {
        const result = await saveUserBet(user.id, bet)
        const { data: savedBet, error } = result
        
        if (error) {
          console.error('Error saving bet:', error)
          alert('Error saving bet: ' + error)
          return
        }
        
        // Update local state with database bet (which has the proper ID)
        const dbBet = {
          id: savedBet.id,
          ...savedBet.bet_data
        }
        
        
        if (bet.type === 'EV') { setEvBets(prev => [...prev, dbBet]); setAllEvBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'ARB') { setArbBets(prev => [...prev, dbBet]); setAllArbBets(prev => [...prev, dbBet]) }
        else if (bet.type === 'MIDDLE') { setMiddleBets(prev => [...prev, dbBet]); setAllMiddleBets(prev => [...prev, dbBet]) }
        else { setPromoBets(prev => [...prev, dbBet]); setAllPromoBets(prev => [...prev, dbBet]) }
      } catch (err) {
        console.error('Exception saving bet:', err)
        alert('Exception saving bet: ' + err.message)
        return
      }
    } else {
      // Fallback to local state if no user
      if (bet.type === 'EV') { setEvBets(prev => [...prev, bet]); setAllEvBets(prev => [...prev, bet]) }
      else if (bet.type === 'ARB') { setArbBets(prev => [...prev, bet]); setAllArbBets(prev => [...prev, bet]) }
      else if (bet.type === 'MIDDLE') { setMiddleBets(prev => [...prev, bet]); setAllMiddleBets(prev => [...prev, bet]) }
      else { setPromoBets(prev => [...prev, bet]); setAllPromoBets(prev => [...prev, bet]) }
    }
    
    closeModal()
  }

  const editBet = (betId) => {
    const allBets = [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets]
    const betToEdit = allBets.find(bet => bet.id === betId)
    if (betToEdit) {
      setNewBet({
        type: betToEdit.type,
        sport: betToEdit.sport,
        match: betToEdit.match,
        selection: betToEdit.selection || '',
        bookmaker: betToEdit.bookmaker,
        bet_type_tag: betToEdit.bet_type_tag || 'single',
        bookmaker2: betToEdit.bookmaker2 || '',
        market_type: betToEdit.market_type || '',
        odds: betToEdit.odds || '',
        stake: betToEdit.stake || '',
        ev_percentage: betToEdit.ev_percentage || '',
        profit: '',
        // A bet with a game time shows that game's date, read only (see the
        // Date field); the rest keep the placed date they always had.
        date: betToEdit.commence_time ? betDate(betToEdit).toLocaleDateString('en-CA') : placedDateInput(betToEdit),
        promo_type: betToEdit.promo_type || 'Mug Bet',
        bonus_received: betToEdit.bonus_received || false,
        bonus_amount: betToEdit.bonus_amount || '',
        free_bonus: betToEdit.free_bonus || false,
        qualifying_loss: betToEdit.qualifying_loss || ''
      })
      setEditingBetId(betId)
      setEditingBetOriginal(betToEdit)
      setShowAddForm(true)
    }
  }

  const updateBet = async () => {
    if (!newBet.match || !newBet.stake) return
    if (newBet.type === 'EV' && (!newBet.odds || !newBet.selection || !newBet.bookmaker)) return
    if (newBet.type === 'ARB' && (!newBet.bookmaker || !newBet.bookmaker2)) return
    if (newBet.type === 'PROMO' && !newBet.bookmaker) return
    
    const updatedBetData = {
      // Original first so fields the form does not carry are preserved; the form
      // values below then overwrite everything the user can actually change.
      ...(editingBetOriginal || {}),
      ...newBet,
      odds: newBet.odds ? parseFloat(newBet.odds) : null,
      stake: parseFloat(newBet.stake),
      ev_percentage: newBet.ev_percentage ? parseFloat(newBet.ev_percentage) : null,
      bonus_amount: newBet.bonus_amount ? parseFloat(newBet.bonus_amount) : null,
      status: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? (parseFloat(newBet.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? parseFloat(newBet.profit) : null,
      settled_at: (newBet.type === 'ARB' || newBet.type === 'PROMO' || newBet.type === 'MIDDLE') && newBet.profit ? new Date(newBet.date).toISOString() : null,
      // Kept as saved unless the user actually moved the date: rewriting it on
      // every edit reset the exact tracked time to midnight. A bet with a game
      // time cannot move its date at all.
      placed_at: (editingBetOriginal?.placed_at && (editingBetOriginal.commence_time || newBet.date === placedDateInput(editingBetOriginal)))
        ? editingBetOriginal.placed_at
        : new Date(newBet.date).toISOString()
    }
    delete updatedBetData.id  // id lives on the row, not inside bet_data
    
    // Update in database
    if (user?.id) {
      const { error } = await updateUserBet(editingBetId, updatedBetData)
      if (error) {
        console.error('Error updating bet:', error)
        return
      }
    }
    
    // Update local state
    const updatedBet = { 
      id: editingBetId,
      ...updatedBetData
    }
    
    if (newBet.type === 'EV') {
      setEvBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllEvBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else if (newBet.type === 'ARB') {
      setArbBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllArbBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else if (newBet.type === 'MIDDLE') {
      setMiddleBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllMiddleBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    } else {
      setPromoBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
      setAllPromoBets(prev => prev.map(bet => bet.id === editingBetId ? updatedBet : bet))
    }
    closeModal()
  }

  const closeModal = () => {
    setShowAddForm(false)
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)
    setShowTimeDropdown(false)
    setShowSportDropdown(false)
    setShowBookmakerFilterDropdown(false)
    setEditingBetId(null)
    setEditingBetOriginal(null)
    setNewBet({ type: activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'ARB' : activeTab === 'MIDDLE' ? 'MIDDLE' : 'PROMO', sport: 'NBA', match: '', selection: '', bookmaker: '', bookmaker2: '', market_type: '', odds: '', stake: '', ev_percentage: '', profit: '', qualifying_loss: '', date: new Date().toISOString().split('T')[0], promo_type: 'Mug Bet', bonus_received: false, bonus_amount: '', free_bonus: false, bet_type_tag: 'single' })
  }

  const selectBookmaker = (bookmaker, field = 'bookmaker') => {
    setNewBet({...newBet, [field]: bookmaker})
    setShowBookmakerDropdown(false)
    setShowBookmaker2Dropdown(false)
  }

  const updateBetStatus = async (betId, status) => {
    
    // Find the bet to update
    const allBets = [...allEvBets, ...allArbBets, ...allPromoBets, ...allMiddleBets]
    const betToUpdate = allBets.find(bet => bet.id === betId)
    if (!betToUpdate) {
      console.error('Bet not found:', betId)
      return
    }
    
    
    // Calculate updated bet data
    let updatedBetData
    if (isHorseBet(betToUpdate)) {
      // Horse Racing Bot bets: status here is won / placed / lost
      updatedBetData = settleHorseBet(betToUpdate, status)
    } else if (betToUpdate.type === 'ARB' || betToUpdate.type === 'PROMO' || betToUpdate.type === 'MIDDLE') {
      // For ARB, PROMO, and MIDDLE bets, handle profit appropriately
      if (betToUpdate.type === 'MIDDLE' && status === 'lost') {
        // For MIDDLE bets that are lost, use negative qualifying loss
        updatedBetData = { 
          ...betToUpdate, 
          status, 
          actual_profit: -(betToUpdate.qualifying_loss || 0),
          settled_at: status !== 'pending' ? new Date().toISOString() : null 
        }
      } else {
        // For ARB, PROMO, and won MIDDLE bets, keep existing profit
        updatedBetData = { 
          ...betToUpdate, 
          status, 
          settled_at: status !== 'pending' ? new Date().toISOString() : null 
        }
      }
    } else {
      // For EV bets, calculate profit based on won/lost
      updatedBetData = { 
        ...betToUpdate, 
        status, 
        actual_profit: status === 'won' ? (betToUpdate.odds - 1) * betToUpdate.stake : status === 'lost' ? -betToUpdate.stake : null, 
        settled_at: status !== 'pending' ? new Date().toISOString() : null 
      }
    }
    
    
    // Update in database
    if (user?.id) {
      const { error } = await updateUserBet(betId, updatedBetData)
      if (error) {
        console.error('Error updating bet status:', error)
        alert('Error updating bet status: ' + error)
        return
      }
    }
    
    // Update local state based on bet type, not active tab
    if (betToUpdate.type === 'EV') {
      setEvBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllEvBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else if (betToUpdate.type === 'ARB') {
      setArbBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllArbBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else if (betToUpdate.type === 'MIDDLE') {
      setMiddleBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllMiddleBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    } else {
      setPromoBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
      setAllPromoBets(prev => prev.map(bet => bet.id === betId ? updatedBetData : bet))
    }
    
  }

  const deleteBet = async (betId) => {
    
    // Delete from database
    if (user?.id) {
      const result = await deleteUserBet(betId)
      
      if (result.error) {
        console.error('Error deleting bet:', result.error)
        alert('Error deleting bet: ' + result.error)
        return
      }
    }
    
    // Update local state
    if (activeTab === 'EV') { setEvBets(prev => prev.filter(bet => bet.id !== betId)); setAllEvBets(prev => prev.filter(bet => bet.id !== betId)) }
    else if (activeTab === 'ARB') { setArbBets(prev => prev.filter(bet => bet.id !== betId)); setAllArbBets(prev => prev.filter(bet => bet.id !== betId)) }
    else if (activeTab === 'MIDDLE') { setMiddleBets(prev => prev.filter(bet => bet.id !== betId)); setAllMiddleBets(prev => prev.filter(bet => bet.id !== betId)) }
    else { setPromoBets(prev => prev.filter(bet => bet.id !== betId)); setAllPromoBets(prev => prev.filter(bet => bet.id !== betId)) }
    
  }

  return (
    <div style={s.container}>
      <style>{`
        .m-tab-btn {
          padding: 8px 4px;
          border: none;
          border-bottom: 2px solid transparent;
          border-radius: 0;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          transition: color 0.2s ease, border-color 0.2s ease;
          color: #64748b;
          background: transparent;
          white-space: nowrap;
          flex: 1;
          text-align: center;
        }
        .m-tab-btn.active {
          color: #f1f5f9;
          font-weight: 600;
          border-bottom: 2px solid #3b82f6;
        }
        .m-table-btn-edit,
        .m-table-btn-delete,
        .m-table-btn-won,
        .m-table-btn-lost {
          padding: 7px 10px; background: transparent; border-radius: 5px;
          cursor: pointer; display: flex; align-items: center;
          transition: all 0.15s ease;
        }
        .m-table-btn-edit  { color: #60a5fa; border: 1px solid rgba(59, 130, 246, 0.35); }
        .m-table-btn-delete{ color: #8a8f96; border: 1px solid #1b2538; }
        .m-table-btn-won   { color: #34d399; border: 1px solid rgba(16, 185, 129, 0.25); }
        .m-table-btn-lost  { color: #f87171; border: 1px solid rgba(239, 68, 68, 0.25); }

        /* Touch has no hover, so :active is the only feedback these can give. */
        .m-table-btn-edit:active  { background: rgba(59, 130, 246, 0.15); }
        .m-table-btn-delete:active{ background: rgba(255, 255, 255, 0.06); }
        .m-table-btn-won:active   { background: rgba(16, 185, 129, 0.18); }
        .m-table-btn-lost:active  { background: rgba(239, 68, 68, 0.18); }
        @keyframes trackerStatScroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
      <div style={{ ...s.card, borderBottom: 'none', paddingBottom: 0 }}>
        {/* Mobile Header */}
        <div style={{ marginBottom: '16px' }}>
          {/* Tab Buttons + Add Bet */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid #334155', flex: 1 }}>
              {[['EV','EV'],['ARB','Arb'],['MIDDLE','Middle'],['PROMO','Promo']].map(([val, label]) => (
                <button key={val} onClick={() => setActiveTab(val)} className={`m-tab-btn${activeTab === val ? ' active' : ''}`}>{label}</button>
              ))}
            </div>
            <button onClick={() => setShowAddForm(true)} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', border: '1px solid #3b82f6', borderRadius: '6px', cursor: 'pointer', marginLeft: '12px' }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M6 1v10M1 6h10" stroke="#f1f5f9" strokeWidth="1.8" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>
        
        {/* Filter Buttons - Mobile Layout */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '16px'
        }}>
          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('time')} style={{ ...s.filterBtn, width: '100%', border: timeFilter !== 'all' ? '1px solid rgba(59,130,246,0.5)' : '1px solid #1b2538', background: timeFilter !== 'all' ? 'rgba(59,130,246,0.12)' : 'transparent', color: timeFilter !== 'all' ? '#93c5fd' : '#cbd5e1' }}>
              <ChipIcon name="time" color={timeFilter !== 'all' ? '#60a5fa' : '#64748b'} />
              <span style={s.chipLabel}>{timeFilter === 'all' ? 'All Time' : timeFilter === '7days' ? '7 Days' : timeFilter === '1month' ? '1 Month' : 'Custom'}</span>
              <ChipChevron open={showTimeDropdown} />
            </button>
            {showTimeDropdown && (
              <div style={s.dropdown}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div 
                    onClick={() => {setTimeFilter('all'); setShowTimeDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: timeFilter === 'all' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: timeFilter === 'all' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>All Time</span>
                    <RowTick show={timeFilter === 'all'} />
                  </div>
                  <div 
                    onClick={() => {setTimeFilter('7days'); setShowTimeDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: timeFilter === '7days' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: timeFilter === '7days' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>7 Days</span>
                    <RowTick show={timeFilter === '7days'} />
                  </div>
                  <div 
                    onClick={() => {setTimeFilter('1month'); setShowTimeDropdown(false)}}
                    style={{
                      ...s.checkbox,
                      border: timeFilter === '1month' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: timeFilter === '1month' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>1 Month</span>
                    <RowTick show={timeFilter === '1month'} />
                  </div>
                  <div
                    onClick={() => setTimeFilter('custom')}
                    style={{
                      ...s.checkbox,
                      border: timeFilter === 'custom' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: timeFilter === 'custom' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>Custom</span>
                    <RowTick show={timeFilter === 'custom'} />
                  </div>
                  {timeFilter === 'custom' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingTop: '4px' }}>
                      <input
                        type="date"
                        value={customDateStart}
                        onChange={e => setCustomDateStart(e.target.value)}
                        style={{ ...s.input, padding: '8px 10px', fontSize: '13px' }}
                        placeholder="From"
                      />
                      <input
                        type="date"
                        value={customDateEnd}
                        onChange={e => setCustomDateEnd(e.target.value)}
                        style={{ ...s.input, padding: '8px 10px', fontSize: '13px' }}
                        placeholder="To"
                      />
                      <button
                        onClick={() => setShowTimeDropdown(false)}
                        style={{ ...s.btn, padding: '8px', fontSize: '12px' }}
                      >
                        Apply
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('sport')} style={{ ...s.filterBtn, width: '100%', border: sportFilter !== 'all' ? '1px solid rgba(59,130,246,0.5)' : '1px solid #1b2538', background: sportFilter !== 'all' ? 'rgba(59,130,246,0.12)' : 'transparent', color: sportFilter !== 'all' ? '#93c5fd' : '#cbd5e1' }}>
              <ChipIcon name="sport" color={sportFilter !== 'all' ? '#60a5fa' : '#64748b'} />
              <span style={s.chipLabel}>{sportFilter === 'all' ? 'Sports' : sportFilter}</span>
              <ChipChevron open={showSportDropdown} />
            </button>
            {showSportDropdown && (
              <div style={s.dropdown}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div
                    onClick={() => {setSportFilter('all'); setShowSportDropdown(false)}}
                    style={{
                      ...s.checkbox,
                      border: sportFilter === 'all' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: sportFilter === 'all' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>All Sports</span>
                    <RowTick show={sportFilter === 'all'} />
                  </div>

                  {/* Sport groups: plain header + flat league list (matches app FilterChips) */}
                  {Object.entries(SPORT_HIERARCHY).map(([sportName, sportData]) => {
                    // Skip Racing category if not in PROMO tab
                    if (sportName === 'Racing' && activeTab !== 'PROMO') return null

                    return (
                      <div key={sportName} style={{ marginBottom: '8px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <span style={{ padding: '0 4px', fontSize: '12px', fontWeight: '600', color: '#94a3b8' }}>{sportName}</span>
                        {sportData.leagues.map(league => (
                          <div
                            key={league}
                            onClick={() => {setSportFilter(league); setShowSportDropdown(false)}}
                            style={{
                              ...s.checkbox,
                              border: sportFilter === league ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                              backgroundColor: '#0f172a',
                              color: sportFilter === league ? '#93c5fd' : '#e2e8f0'
                            }}
                          >
                            <span>{SPORT_DISPLAY_NAMES[league] || league}</span>
                            <RowTick show={sportFilter === league} />
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
          
          <div className="filter-dropdown" style={{ position: 'relative' }}>
            <button onClick={() => toggleDropdown('bookmakerFilter')} style={{ ...s.filterBtn, width: '100%', border: bookmakerFilter !== 'all' ? '1px solid rgba(59,130,246,0.5)' : '1px solid #1b2538', background: bookmakerFilter !== 'all' ? 'rgba(59,130,246,0.12)' : 'transparent', color: bookmakerFilter !== 'all' ? '#93c5fd' : '#cbd5e1' }}>
              <ChipIcon name="book" color={bookmakerFilter !== 'all' ? '#60a5fa' : '#64748b'} />
              <span style={s.chipLabel}>{bookmakerFilter === 'all' ? 'Bookmakers' : bookmakerFilter}</span>
              <ChipChevron open={showBookmakerFilterDropdown} />
            </button>
            {showBookmakerFilterDropdown && (
              <div style={{ ...s.dropdown, minWidth: '200px', left: 0, right: 'auto', transform: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                  <div 
                    onClick={() => {setBookmakerFilter('all'); setShowBookmakerFilterDropdown(false)}} 
                    style={{
                      ...s.checkbox,
                      border: bookmakerFilter === 'all' ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                      backgroundColor: '#0f172a',
                      color: bookmakerFilter === 'all' ? '#93c5fd' : '#e2e8f0'
                    }}
                  >
                    <span>All Bookmakers</span>
                    <RowTick show={bookmakerFilter === 'all'} />
                  </div>
                  {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                    <div key={country}>
                      <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: '600', marginBottom: '8px', paddingLeft: '8px' }}>{country}</div>
                      {bookmakers.map(bookmaker => (
                        <div 
                          key={bookmaker} 
                          onClick={() => {setBookmakerFilter(bookmaker); setShowBookmakerFilterDropdown(false)}} 
                          style={{
                            ...s.checkbox,
                            border: bookmakerFilter === bookmaker ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                            backgroundColor: '#0f172a',
                      color: bookmakerFilter === bookmaker ? '#93c5fd' : '#e2e8f0'
                          }}
                        >
                          {BOOKMAKER_LOGOS[bookmaker] && <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px' }} />}
                          <span>{bookmaker}</span>
                          <RowTick show={bookmakerFilter === bookmaker} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {activeTab === 'EV' && (
            <div className="filter-dropdown" style={{ position: 'relative' }}>
              <button onClick={() => toggleDropdown('betType')} style={{ ...s.filterBtn, width: '100%', border: betTypeFilter.size !== 5 ? '1px solid rgba(59,130,246,0.5)' : '1px solid #1b2538', background: betTypeFilter.size !== 5 ? 'rgba(59,130,246,0.12)' : 'transparent', color: betTypeFilter.size !== 5 ? '#93c5fd' : '#cbd5e1' }}>
                <ChipIcon name="tags" color={betTypeFilter.size !== 5 ? '#60a5fa' : '#64748b'} />
                <span style={s.chipLabel}>
                  {isOtherMode(betTypeFilter) ? (betTypeFilter.size === 1 ? MOBILE_OTHER_LABELS[[...betTypeFilter][0]] : 'Other') :
                   betTypeFilter.size === MOBILE_TOOL_BET_TYPES.length ? 'All Types' :
                   betTypeFilter.size === 0 ? 'None' :
                   betTypeFilter.size === 1 ? (
                     betTypeFilter.has('single')        ? 'EV Single' :
                     betTypeFilter.has('multi')         ? 'EV Multi' :
                     betTypeFilter.has('dabble_pickem') ? 'PickEm EV' :
                     betTypeFilter.has('prop_ev')       ? 'Prop Outliers' : 'Prop Outlier Multi'
                   ) : `${betTypeFilter.size} Types`}
                </span>
                <ChipChevron open={showBetTypeDropdown} />
              </button>
              {showBetTypeDropdown && (
                <div style={{ ...s.dropdown, minWidth: '150px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div
                      onClick={() => setBetTypeFilter(
                        !isOtherMode(betTypeFilter) && betTypeFilter.size === MOBILE_TOOL_BET_TYPES.length ? new Set() : new Set(MOBILE_TOOL_BET_TYPES)
                      )}
                      style={{
                        ...s.checkbox,
                        border: !isOtherMode(betTypeFilter) && betTypeFilter.size === MOBILE_TOOL_BET_TYPES.length ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                        backgroundColor: '#0f172a',
                        color: !isOtherMode(betTypeFilter) && betTypeFilter.size === MOBILE_TOOL_BET_TYPES.length ? '#93c5fd' : '#e2e8f0',
                        marginBottom: '4px'
                      }}
                    >
                      <span>All Types</span>
                      <RowTick show={!isOtherMode(betTypeFilter) && betTypeFilter.size === MOBILE_TOOL_BET_TYPES.length} />
                    </div>
                    {[
                      { key: 'single',        label: 'EV Single' },
                      { key: 'multi',         label: 'EV Multi' },
                      { key: 'dabble_pickem', label: 'PickEm EV' },
                      { key: 'prop_ev',       label: 'Prop Outliers' },
                      { key: 'prop_ev_multi', label: 'Prop Outlier Multi' },
                    ].map(({ key, label }) => {
                      const active = betTypeFilter.has(key)
                      return (
                        <div
                          key={key}
                          onClick={() => {
                            // Leaving odds-screen mode starts a fresh set, so
                            // the two can never be selected together.
                            const next = isOtherMode(betTypeFilter) ? new Set() : new Set(betTypeFilter)
                            active && !isOtherMode(betTypeFilter) ? next.delete(key) : next.add(key)
                            setBetTypeFilter(next)
                          }}
                          style={{
                            ...s.checkbox,
                            border: active ? '1px solid rgba(59,130,246,0.6)' : '1px solid #1b2538',
                            backgroundColor: '#0f172a',
                            color: active ? '#93c5fd' : '#e2e8f0',
                            display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          <span>{label}</span>
                          <RowTick show={active} />
                        </div>
                      )
                    })}

                    {/* Kept apart, and deliberately quiet. These came off the
                        odds table rather than a tool, so they carry no edge and
                        cannot share a stats block with the ones above. Selecting
                        one is a MODE SWITCH, not another checkbox. */}
                    <div style={{ margin: '6px 0 2px', paddingTop: '8px', borderTop: '1px solid #1b2538', fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' }}>
                      Other
                    </div>
                    {MOBILE_OTHER_BET_TYPES.map((key) => {
                      const label = MOBILE_OTHER_LABELS[key]
                      const active = betTypeFilter.has(key)
                      return (
                        <div
                          key={key}
                          onClick={() => setBetTypeFilter(toggleOtherType(betTypeFilter, key, MOBILE_TOOL_BET_TYPES))}
                          style={{
                            ...s.checkbox,
                            border: active ? '1px solid rgba(34,211,238,0.6)' : '1px solid #1b2538',
                            backgroundColor: '#0f172a',
                            color: active ? '#67e8f9' : '#e2e8f0',
                            display: 'flex', alignItems: 'center', gap: '8px'
                          }}
                        >
                          <span>{label}</span>
                          <RowTick show={active} />
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...s.card, borderBottom: 'none', paddingTop: 0 }}>
        {/* Performance Overview */}
        <div>
        {/* Stats — auto-scrolling marquee strip (matches app: items rendered
            twice, slides left continuously, seamless wrap, no touch needed). */}
        {(() => {
          const statItems = [
            { value: `$${stats.totalProfit.toFixed(2)}`, label: 'Total Profit', color: stats.totalProfit >= 0 ? '#10b981' : '#ef4444' },
            ...(activeTab !== 'PROMO' ? [{ value: `${stats.roi.toFixed(1)}%`, label: 'ROI', color: stats.roi >= 0 ? '#10b981' : '#ef4444' }] : []),
            ...(activeTab === 'EV' && !isOtherMode(betTypeFilter) ? [{ value: `$${stats.theoreticalEV.toFixed(2)}`, label: 'Expected Profit', color: '#f59e0b' }] : []),
            { value: `$${stats.totalStake.toFixed(2)}`, label: 'Total Staked', color: '#f1f5f9' },
            ...(activeTab === 'PROMO' ? [
              { value: `${stats.bonusRetention.toFixed(1)}%`, label: 'Bonus Retention', color: stats.bonusRetention >= 0 ? '#10b981' : '#ef4444' },
              { value: `$${stats.pendingBonusTurnover.toFixed(2)}`, label: 'Pending Turnover', color: '#f59e0b' },
              { value: `${stats.promoMugRatio}:1`, label: 'Promo:Mug Ratio', color: '#f1f5f9' },
            ] : []),
            { value: `${stats.totalBets}`, label: 'Total Bets', color: '#f1f5f9' },
          ]
          return (
            <div style={{ marginTop: '8px', overflow: 'hidden' }}>
              <div style={s.statMarquee}>
                {[0, 1].map(copy => (
                  <div key={copy} style={{ display: 'flex', gap: '8px', paddingRight: '8px', flexShrink: 0 }} aria-hidden={copy === 1}>
                    {statItems.map((it, i) => (
                      <div key={`${copy}-${i}`} style={s.statCard}>
                        <div style={s.statLabel}>{it.label}</div>
                        <div style={{ ...s.statValue, color: it.color }}>{it.value}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )
        })()}
        </div>

        {/* Profit Over Time Chart */}
        <div style={{ borderTop: '1px solid #1b2538', marginTop: '24px', paddingTop: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <span style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9' }}>Profit Over Time</span>
          {activeTab === 'EV' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '9999px', background: '#161f33', padding: '4px 8px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '4px', background: '#10b981' }} />
                <span style={{ fontSize: '10px', fontWeight: '500', color: '#94a3b8' }}>Actual</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', borderRadius: '9999px', background: '#161f33', padding: '4px 8px' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '4px', background: '#f59e0b' }} />
                <span style={{ fontSize: '10px', fontWeight: '500', color: '#94a3b8' }}>Expected</span>
              </div>
            </div>
          )}
        </div>
        {isVisible && (
          <div style={{ height: '300px', width: '100%' }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={300} minWidth={200}>
              <ComposedChart data={chartData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
              {(() => {
                const vals = chartData.map(d => d.profit).filter(v => v != null && !isNaN(v));
                const max = vals.length > 0 ? Math.max(0, ...vals) : 0;
                const min = vals.length > 0 ? Math.min(0, ...vals) : 0;
                const pct = max > min ? Math.round((max / (max - min)) * 100) : 100;
                const color = activeTab === 'EV' ? '#10b981' : '#f59e0b';
                return (
                  <defs>
                    <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
                      <stop offset={`${pct}%`} stopColor={color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                );
              })()}
              <CartesianGrid strokeDasharray="3 3" stroke="#475569" vertical={false} />
              <XAxis
                dataKey="bet"
                stroke="#94a3b8"
                ticks={(() => {
                  const step = chartData.length <= 50 ? 10 : chartData.length <= 200 ? 25 : 100;
                  const result = [0];
                  for (let i = step; i <= chartData.length; i += step) result.push(i);
                  return result;
                })()}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => value.toString()}
              />
              <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} width={30} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#334155', border: '1px solid #1b2538', borderRadius: '8px', color: '#f1f5f9' }}
                formatter={(value, name) => {
                  if (name === 'profit') {
                    return [`$${value.toFixed(2)}`, 'Actual Profit']
                  }
                  if (name === 'theoreticalEV') {
                    return [`$${value.toFixed(2)}`, 'Expected Profit']
                  }
                  return [value, name]
                }}
                labelFormatter={() => ''}
              />
              <Area
                type="monotone"
                dataKey="profit"
                stroke={activeTab === 'EV' ? '#10b981' : '#f59e0b'}
                fill="url(#profitGradient)"
                fillOpacity={1}
                strokeWidth={3}
                dot={
                  chartData.length <= 20 ? { fill: activeTab === 'EV' ? '#10b981' : '#f59e0b', strokeWidth: 2, r: 4 } :
                  chartData.length <= 50 ? { fill: activeTab === 'EV' ? '#10b981' : '#f59e0b', strokeWidth: 1, r: 2 } :
                  false
                }
              />
              {activeTab === 'EV' && !isOtherMode(betTypeFilter) && (
                <Line
                  type="monotone"
                  dataKey="theoreticalEV"
                  stroke="#f59e0b"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={
                    chartData.length <= 20 ? { fill: '#f59e0b', strokeWidth: 2, r: 3 } :     // Medium dots for small datasets
                    chartData.length <= 50 ? { fill: '#f59e0b', strokeWidth: 1, r: 1.5 } :   // Tiny dots for medium datasets
                    false                                                                     // No dots for large datasets
                  }
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        )}
        {/* Calendar */}
        {(() => {
          const year = calendarDate.getFullYear()
          const month = calendarDate.getMonth()
          const daysInMonth = new Date(year, month + 1, 0).getDate()
          const firstDow = (new Date(year, month, 1).getDay() + 6) % 7
          const monthName = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })
          const days = ['Mo','Tu','We','Th','Fr','Sa','Su']
          const cells = []
          for (let i = 0; i < firstDow; i++) cells.push(null)
          for (let d = 1; d <= daysInMonth; d++) cells.push(d)
          const monthPrefix = `${year}-${String(month+1).padStart(2,'0')}-`
          const monthDays = Object.entries(calendarDailyProfits).filter(([k]) => k.startsWith(monthPrefix))
          const monthTotal = monthDays.reduce((sum, [, v]) => sum + v, 0)
          // Flat fill, won or lost, no scaling by size. Matches desktop.
          const DAY_FILL = 0.45
          const bestDay = monthDays.reduce((b, [, v]) => (v > b ? v : b), 0)
          const worstDay = monthDays.reduce((b, [, v]) => (v < b ? v : b), 0)
          return (
            <div style={{ marginTop: '16px', backgroundColor: 'transparent', border: '1px solid #1b2538', borderRadius: '10px', padding: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #1b2538', background: 'transparent', color: '#8a8f96', cursor: 'pointer', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '0 10px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#f1f5f9' }}>{monthName}</span>
                </div>
                <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', borderRadius: '8px', border: '1px solid #1b2538', background: 'transparent', color: '#8a8f96', cursor: 'pointer', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', borderBottom: '1px solid #1b2538', paddingBottom: '8px', marginBottom: '6px' }}>
                {days.map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '10px', color: '#8a8f96', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
                {cells.map((day, i) => {
                  if (!day) return <div key={`e${i}`} />
                  const key = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                  const profit = calendarDailyProfits[key]
                  const hasBets = profit !== undefined
                  const isPositive = profit > 0
                  const today = new Date()
                  const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day
                  return (
                    <div key={key} title={hasBets ? `$${profit.toFixed(2)}` : 'No bets'} style={{
                      minHeight: '48px',
                      borderRadius: '8px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '4px 6px',
                      boxSizing: 'border-box',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      // One neutral border on every cell; the fill alone says won or lost.
                      borderColor: isToday ? '#3b82f6' : '#1b2538',
                      backgroundColor: hasBets ? `rgba(${isPositive ? '16,185,129' : '239,68,68'},${DAY_FILL})` : 'transparent',
                    }}>
                      <span style={{ fontSize: '11px', fontFamily: MONO, fontWeight: '500', color: hasBets ? (isPositive ? '#6ee7b7' : '#fca5a5') : '#475569' }}>{day}</span>
                      {hasBets && <span style={{ fontSize: '10px', fontFamily: MONO, fontWeight: '600', lineHeight: 1, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: isPositive ? '#34d399' : '#f87171' }}>{isPositive ? '+' : '-'}${Math.abs(profit >= 100 || profit <= -100 ? Math.round(profit) : parseFloat(profit.toFixed(1)))}</span>}
                    </div>
                  )
                })}
              </div>

              {/* Same three figures the desktop calendar reports, stacked to fit. */}
              {monthDays.length > 0 && (
                <div style={{ display: 'flex', gap: '10px', borderTop: '1px solid #1b2538', marginTop: '10px', paddingTop: '10px' }}>
                  {[
                    { label: 'Total', value: `${monthTotal >= 0 ? '+' : '-'}$${Math.abs(monthTotal).toFixed(2)}`, color: monthTotal >= 0 ? '#34d399' : '#f87171' },
                    { label: 'Best day', value: `+$${bestDay.toFixed(2)}`, color: '#34d399' },
                    { label: 'Worst day', value: worstDay < 0 ? `-$${Math.abs(worstDay).toFixed(2)}` : '$0.00', color: worstDay < 0 ? '#f87171' : '#8a8f96' }
                  ].map(stat => (
                    <div key={stat.label} style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '9px', fontFamily: SANS, fontWeight: '600', color: '#8a8f96', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{stat.label}</div>
                      <div style={{ fontSize: '12px', fontFamily: MONO, fontWeight: '500', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em', color: stat.color }}>{stat.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}
        </div>
      </div>

      {showAddForm && (
        <div style={s.modalOverlay} onClick={closeModal}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.subtitle}>{editingBetId ? 'Edit Bet' : 'Add New Bet'}</h2>
              <button onClick={closeModal} style={s.closeBtn}>✕</button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Bet Type</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    { value: 'EV', label: 'EV' },
                    { value: 'ARB', label: 'Arbitrage' },
                    { value: 'MIDDLE', label: 'Middle' },
                    { value: 'PROMO', label: 'Promo' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setNewBet({ ...newBet, type: opt.value })}
                      style={newBet.type === opt.value ? s.pillActive : s.pill}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Sport</label>
                <select value={newBet.sport} onChange={(e) => setNewBet({...newBet, sport: e.target.value})} style={s.select}>
                  {Object.entries(SPORT_HIERARCHY).map(([sportName, sportData]) => {
                    // Skip Racing category if not in PROMO tab
                    if (sportName === 'Racing' && newBet.type !== 'PROMO') return null

                    return (
                      <optgroup key={sportName} label={sportName}>
                        {sportData.leagues.map(league => (
                          <option key={league} value={league}>
                            {SPORT_DISPLAY_NAMES[league] || league}
                          </option>
                        ))}
                      </optgroup>
                    )
                  })}
                </select>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Match</label>
                <input type="text" placeholder="Lakers vs Warriors" value={newBet.match} onChange={(e) => setNewBet({...newBet, match: e.target.value})} style={s.input} />
              </div>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Date</label>
                <input type="date" value={newBet.date} onChange={(e) => setNewBet({...newBet, date: e.target.value})} disabled={!!(editingBetId && editingBetOriginal?.commence_time)} title={editingBetId && editingBetOriginal?.commence_time ? 'Set by the game\'s start time' : undefined} style={editingBetId && editingBetOriginal?.commence_time ? { ...s.input, opacity: 0.6, cursor: 'not-allowed' } : s.input} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>{newBet.type === 'EV' ? 'Selection' : newBet.type === 'PROMO' ? 'Promo Type' : 'Market'}</label>
                {newBet.type === 'PROMO' ? (
                  <select value={newBet.promo_type} onChange={(e) => setNewBet({...newBet, promo_type: e.target.value})} style={s.select}>
                    <option value="Mug Bet">Mug Bet</option>
                    <option value="Promo Bet">Promo Bet</option>
                    <option value="Bonus Turnover">Bonus Turnover</option>
                  </select>
                ) : (
                  <input type="text" placeholder={newBet.type === 'EV' ? "LeBron Over 25.5 Points" : "Head To Head"} value={newBet.type === 'EV' ? newBet.selection : newBet.market_type} onChange={(e) => setNewBet({...newBet, [newBet.type === 'EV' ? 'selection' : 'market_type']: e.target.value})} style={s.input} />
                )}
              </div>
              {newBet.type === 'EV' && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Market</label>
                  <input type="text" placeholder="Player Points" value={newBet.market_type} onChange={(e) => setNewBet({...newBet, market_type: e.target.value})} style={s.input} />
                </div>
              )}
              {(newBet.type === 'ARB' || newBet.type === 'MIDDLE') && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Total Stake ($)</label>
                  <input type="number" step="0.01" placeholder="200" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                </div>
              )}
              {newBet.type === 'PROMO' && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Stake ($)</label>
                  <input type="number" step="0.01" placeholder="100" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
              <div>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>{(newBet.type === 'EV' || newBet.type === 'PROMO') ? 'Bookmaker' : 'Bookmaker 1'}</label>
                <div className="bookmaker-dropdown" style={{ position: 'relative' }}>
                  <button type="button" onClick={() => toggleDropdown('bookmaker')} style={s.dropdownButton}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {newBet.bookmaker && BOOKMAKER_LOGOS[newBet.bookmaker] && <img src={BOOKMAKER_LOGOS[newBet.bookmaker]} alt={newBet.bookmaker} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />}
                      <span>{newBet.bookmaker || 'Select Bookmaker'}</span>
                    </div>
                    <span>{showBookmakerDropdown ? '▲' : '▼'}</span>
                  </button>
                  {showBookmakerDropdown && (
                    <div style={s.dropdownList}>
                      {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                        <div key={country}>
                          <div style={{ padding: '8px 16px', backgroundColor: '#0b1322', color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>{country}</div>
                          {bookmakers.map(bookmaker => (
                            <div
                              key={bookmaker}
                              onClick={() => selectBookmaker(bookmaker)}
                              style={{
                                ...s.dropdownItem,
                                backgroundColor: newBet.bookmaker === bookmaker ? '#13203a' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 12px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                border: newBet.bookmaker === bookmaker ? '1px solid #3b82f6' : '1px solid transparent'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#13203a'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = newBet.bookmaker === bookmaker ? '#13203a' : 'transparent'}
                            >
                              {BOOKMAKER_LOGOS[bookmaker] ? (
                                <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} />
                              ) : (
                                <div style={{ width: '24px', height: '24px', backgroundColor: '#0b1322', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#94a3b8' }}>
                                  {bookmaker.slice(0, 3).toUpperCase()}
                                </div>
                              )}
                              <span style={{ color: '#f1f5f9', fontSize: '14px' }}>{bookmaker}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {(newBet.type === 'ARB' || newBet.type === 'MIDDLE') && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Bookmaker 2</label>
                  <div className="bookmaker-dropdown" style={{ position: 'relative' }}>
                    <button type="button" onClick={() => toggleDropdown('bookmaker2')} style={s.dropdownButton}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {newBet.bookmaker2 && BOOKMAKER_LOGOS[newBet.bookmaker2] && <img src={BOOKMAKER_LOGOS[newBet.bookmaker2]} alt={newBet.bookmaker2} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />}
                        <span>{newBet.bookmaker2 || 'Select Bookmaker 2'}</span>
                      </div>
                      <span>{showBookmaker2Dropdown ? '▲' : '▼'}</span>
                    </button>
                    {showBookmaker2Dropdown && (
                      <div style={s.dropdownList}>
                        {Object.entries(BOOKMAKERS).map(([country, bookmakers]) => (
                          <div key={country}>
                            <div style={{ padding: '8px 16px', backgroundColor: '#0b1322', color: '#94a3b8', fontSize: '12px', fontWeight: '600' }}>{country}</div>
                            {bookmakers.map(bookmaker => (
                              <div
                                key={bookmaker}
                                onClick={() => selectBookmaker(bookmaker, 'bookmaker2')}
                                style={{
                                  ...s.dropdownItem,
                                  backgroundColor: newBet.bookmaker2 === bookmaker ? '#13203a' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderRadius: '4px',
                                  border: newBet.bookmaker2 === bookmaker ? '1px solid #3b82f6' : '1px solid transparent'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#13203a'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = newBet.bookmaker2 === bookmaker ? '#13203a' : 'transparent'}
                              >
                                {BOOKMAKER_LOGOS[bookmaker] ? (
                                  <img src={BOOKMAKER_LOGOS[bookmaker]} alt={bookmaker} style={{ width: '24px', height: '24px', borderRadius: '4px', objectFit: 'contain' }} />
                                ) : (
                                  <div style={{ width: '24px', height: '24px', backgroundColor: '#0b1322', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: '#94a3b8' }}>
                                    {bookmaker.slice(0, 3).toUpperCase()}
                                  </div>
                                )}
                                <span style={{ color: '#f1f5f9', fontSize: '14px' }}>{bookmaker}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {newBet.type === 'EV' && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>EV %</label>
                  <input type="number" step="0.01" placeholder="5.5" value={newBet.ev_percentage} onChange={(e) => setNewBet({...newBet, ev_percentage: e.target.value})} style={s.input} />
                </div>
              )}

              {newBet.type === 'PROMO' && (
                <div>
                  <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Profit ($)</label>
                  <input type="number" step="0.01" placeholder="15.50" value={newBet.profit} onChange={(e) => setNewBet({...newBet, profit: e.target.value})} style={s.input} />
                </div>
              )}

            </div>

            {(newBet.type === 'EV' || newBet.type === 'ARB' || newBet.type === 'MIDDLE') && (
              <div style={{ display: 'grid', gridTemplateColumns: newBet.type === 'EV' ? '1fr 1fr' : newBet.type === 'MIDDLE' ? '1fr 1fr' : '1fr', gap: '16px', marginTop: '16px' }}>
                {newBet.type === 'EV' && (
                  <>
                    <div>
                      <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Odds</label>
                      <input type="number" step="0.01" placeholder="1.90" value={newBet.odds} onChange={(e) => setNewBet({...newBet, odds: e.target.value})} style={s.input} />
                    </div>
                    <div>
                      <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Stake ($)</label>
                      <input type="number" step="0.01" placeholder="100" value={newBet.stake} onChange={(e) => setNewBet({...newBet, stake: e.target.value})} style={s.input} />
                    </div>
                  </>
                )}
                {newBet.type === 'ARB' && (
                  <div>
                    <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Profit ($)</label>
                    <input type="number" step="0.01" placeholder="15.50" value={newBet.profit} onChange={(e) => setNewBet({...newBet, profit: e.target.value})} style={s.input} />
                  </div>
                )}
                {newBet.type === 'MIDDLE' && (
                  <>
                    <div>
                      <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Profit ($)</label>
                      <input type="number" step="0.01" placeholder="15.50" value={newBet.profit} onChange={(e) => setNewBet({...newBet, profit: e.target.value})} style={s.input} />
                    </div>
                    <div>
                      <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>QL ($)</label>
                      <input type="number" step="0.01" placeholder="10" value={newBet.qualifying_loss} onChange={(e) => setNewBet({...newBet, qualifying_loss: e.target.value})} style={s.input} />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Bet Type for EV bets */}
            {newBet.type === 'EV' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Bet Type</label>
                <select
                  value={newBet.bet_type_tag}
                  onChange={(e) => setNewBet({...newBet, bet_type_tag: e.target.value})}
                  style={s.select}
                >
                  <option value="single">EV Single</option>
                  <option value="multi">EV Multi</option>
                  <option value="dabble_pickem">PickEm EV</option>
                  <option value="prop_ev">Prop Outliers</option>
                  <option value="prop_ev_multi">Prop Outlier Multi</option>
                  <option value="odds_screen">Odds Screen</option>
                  <option value="odds_screen_multi">Odds Multi</option>
                  <option value="racing_odds">Racing Odds</option>
                </select>
              </div>
            )}

            {/* Bonus fields for PROMO bets - positioned below row 3 */}
            {newBet.type === 'PROMO' && newBet.promo_type === 'Promo Bet' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={newBet.bonus_received}
                    onChange={(e) => setNewBet({...newBet, bonus_received: e.target.checked})}
                  />
                  Bonus Received?
                </label>
                {newBet.bonus_received && (
                  <div style={{ marginTop: '8px' }}>
                    <label style={{ ...s.label, display: 'block', marginBottom: '8px' }}>Bonus Amount ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="50"
                      value={newBet.bonus_amount}
                      onChange={(e) => setNewBet({...newBet, bonus_amount: e.target.value})}
                      style={s.input}
                    />
                  </div>
                )}
              </div>
            )}

            {newBet.type === 'PROMO' && newBet.promo_type === 'Bonus Turnover' && (
              <div style={{ marginTop: '16px' }}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    checked={newBet.free_bonus}
                    onChange={(e) => setNewBet({...newBet, free_bonus: e.target.checked})}
                  />
                  Free Bonus (no qualifying bet)
                </label>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button onClick={closeModal} style={{ ...s.btnSecondary, flex: 1 }}>Cancel</button>
              <button onClick={editingBetId ? updateBet : addBet} style={{ ...s.btn, flex: 1 }}>{editingBetId ? 'Update Bet' : 'Save Bet'}</button>
            </div>
          </div>
        </div>
      )}

      <div ref={recentBetsRef} style={s.card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <h2 style={s.subtitle}>Recent {activeTab === 'EV' ? 'EV' : activeTab === 'ARB' ? 'Arbitrage' : activeTab === 'MIDDLE' ? 'Middle' : 'Promo'} Bets</h2>
          <button
            onClick={() => setStatusFilter(f => f === 'all' ? 'pending' : f === 'pending' ? 'won' : f === 'won' ? 'lost' : 'all')}
            style={{ padding: '5px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', border: `1px solid ${statusFilter === 'pending' ? '#f59e0b' : statusFilter === 'won' ? '#10b981' : statusFilter === 'lost' ? '#ef4444' : '#475569'}`, background: 'transparent', color: statusFilter === 'pending' ? '#f59e0b' : statusFilter === 'won' ? '#10b981' : statusFilter === 'lost' ? '#ef4444' : '#94a3b8', cursor: 'pointer' }}
          >
            {statusFilter === 'all' ? 'All Status' : statusFilter === 'pending' ? '● Pending' : statusFilter === 'won' ? '● Won' : '● Lost'}
          </button>
        </div>
        <div style={{ marginTop: '4px' }}>
          {currentBets.length === 0 ? (
            <div style={s.empty}>No bets found for the selected filters.</div>
          ) : (
            paginatedBets.map(bet => {
              // Where this bet stands right now, while its game is in play, and
              // through the gap between it being decided and being settled.
              // Null for anything not started or already settled.
              //
              // A multi resolves per LEG instead: it has one fixture per leg, so
              // liveGames stores { legEntries } for it and liveLegs reads that
              // back. Exactly one of these is ever non-null.
              const matched = activeTab === 'EV' ? liveGames[bet.id] : null
              const legs = matched ? liveLegs(bet, matched) : null
              const live = (!legs && matched) ? liveProgress(bet, matched) : null

              return (
              <div key={bet.id} style={s.betCard}>
                <div style={s.betCardHeader}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                    <h3 style={{ ...s.betCardMatch, flex: 1 }}>{bet.match}</h3>
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '700',
                      whiteSpace: 'nowrap',
                      color: bet.actual_profit > 0 ? '#34d399' : bet.actual_profit < 0 ? '#f87171' : '#94a3b8'
                    }}>
                      {bet.actual_profit != null ? `${bet.actual_profit >= 0 ? '+' : '-'}$${Math.abs(bet.actual_profit).toFixed(2)}` : '—'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '8px' }}>
                    {activeTab === 'EV' && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.03em', textTransform: 'uppercase',
                        backgroundColor: bet.bet_type_tag === 'multi' ? 'rgba(16,185,129,0.1)' : bet.bet_type_tag === 'dabble_pickem' ? 'rgba(168,85,247,0.1)' : bet.bet_type_tag === 'odds_screen' || bet.bet_type_tag === 'odds_screen_multi' ? 'rgba(34,211,238,0.1)' : bet.bet_type_tag === 'racing_odds' ? 'rgba(167,139,250,0.1)' : bet.bet_type_tag === 'prop_ev' || bet.bet_type_tag === 'prop_ev_multi' ? 'rgba(245,158,11,0.1)' : 'rgba(100,116,139,0.1)',
                        color: bet.bet_type_tag === 'multi' ? '#34d399' : bet.bet_type_tag === 'dabble_pickem' ? '#c084fc' : bet.bet_type_tag === 'odds_screen' || bet.bet_type_tag === 'odds_screen_multi' ? '#22d3ee' : bet.bet_type_tag === 'racing_odds' ? '#a78bfa' : bet.bet_type_tag === 'prop_ev' || bet.bet_type_tag === 'prop_ev_multi' ? '#f59e0b' : '#94a3b8',
                        border: `1px solid ${bet.bet_type_tag === 'multi' ? 'rgba(16,185,129,0.25)' : bet.bet_type_tag === 'dabble_pickem' ? 'rgba(168,85,247,0.25)' : bet.bet_type_tag === 'odds_screen' || bet.bet_type_tag === 'odds_screen_multi' ? 'rgba(34,211,238,0.25)' : bet.bet_type_tag === 'racing_odds' ? 'rgba(167,139,250,0.25)' : bet.bet_type_tag === 'prop_ev' || bet.bet_type_tag === 'prop_ev_multi' ? 'rgba(245,158,11,0.25)' : 'rgba(100,116,139,0.2)'}`,
                      }}>
                        {bet.bet_type_tag === 'multi' ? 'EV Multi' : bet.bet_type_tag === 'dabble_pickem' ? 'PickEm EV' : bet.bet_type_tag === 'odds_screen' ? 'Odds' : bet.bet_type_tag === 'odds_screen_multi' ? 'Odds Multi' : bet.bet_type_tag === 'racing_odds' ? 'Racing' : bet.bet_type_tag === 'prop_ev' ? 'Outlier' : bet.bet_type_tag === 'prop_ev_multi' ? 'Outlier Multi' : 'EV Single'}
                      </span>
                    )}
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: '4px',
                      padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.03em', textTransform: 'uppercase',
                      backgroundColor: bet.status === 'won' ? 'rgba(16,185,129,0.1)' : bet.horse_result === 'PLACED' ? 'rgba(245,158,11,0.1)' : bet.status === 'lost' ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.1)',
                      color: bet.status === 'won' ? '#34d399' : bet.horse_result === 'PLACED' ? '#fbbf24' : bet.status === 'lost' ? '#f87171' : '#94a3b8',
                      border: `1px solid ${bet.status === 'won' ? 'rgba(16,185,129,0.25)' : bet.horse_result === 'PLACED' ? 'rgba(245,158,11,0.25)' : bet.status === 'lost' ? 'rgba(239,68,68,0.25)' : 'rgba(100,116,139,0.2)'}`,
                    }}>
                      <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'currentColor' }} />
                      {bet.horse_result === 'PLACED' ? 'placed' : bet.status}
                    </span>
                    {activeTab === 'EV' && bet.ev_percentage != null && bet.ev_percentage !== '' && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700', letterSpacing: '0.03em', textTransform: 'uppercase',
                        backgroundColor: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.4)', color: '#60a5fa'
                      }}>
                        {bet.ev_percentage}% EV
                      </span>
                    )}
                  </div>
                </div>

                <div style={s.betCardRow}>
                  <span style={s.cardLabel}>Date</span>
                  <span style={s.cardValue}>{betDate(bet).toLocaleDateString()}</span>
                </div>

                {activeTab === 'EV' && (
                  <>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Selection</span>
                      <span style={s.cardValue} title={bet.selection}>
                        {bet.selection && bet.selection.length > 40 ? bet.selection.substring(0, 40) + '...' : bet.selection}
                      </span>
                    </div>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Market</span>
                      <span style={s.cardValue}>{formatMarketType(bet.market_type || '') || '-'}</span>
                    </div>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Bookmaker</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {BOOKMAKER_LOGOS[bet.bookmaker] && (
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />
                        )}
                        <span style={s.cardValue}>{bet.bookmaker}</span>
                      </div>
                    </div>
                    <div style={s.betCardRowLast}>
                      <span style={s.cardLabel}>Odds / Stake</span>
                      <span style={s.cardValue}>
                        {convertOdds(bet.odds, oddsFormat)}
                        <span style={{ color: '#475569' }}>{'  /  '}</span>
                        ${bet.stake}
                      </span>
                    </div>
                  </>
                )}

                {/* Under the card's own details, the same place the desktop
                    panel sits relative to its row. Above them it would split the
                    label/value list in two, and the panel is a different kind of
                    thing from a row of facts: it moves.

                    A multi waits on a leg actually being live, matching desktop.
                    Its own strip is worth showing while a game runs; a multi
                    whose legs are all days away has nothing to put on it. */}
                {legs
                  ? (legs.some(l => l.live) && <LiveMultiCard legs={legs} open={openMultis.has(bet.id)} onToggle={() => toggleMulti(bet.id)} />)
                  : <LiveBetCard live={live} bet={bet} />}

                {(activeTab === 'ARB' || activeTab === 'MIDDLE') && (
                  <>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Market</span>
                      <span style={s.cardValue}>{formatMarketType(bet.market_type || '') || '-'}</span>
                    </div>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Bookmakers</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {BOOKMAKER_LOGOS[bet.bookmaker] && (
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />
                        )}
                        {BOOKMAKER_LOGOS[bet.bookmaker2] && (
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker2]} alt={bet.bookmaker2} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />
                        )}
                        <span style={s.cardValue}>{bet.bookmaker} vs {bet.bookmaker2}</span>
                      </div>
                    </div>
                    <div style={s.betCardRowLast}>
                      <span style={s.cardLabel}>Total Stake</span>
                      <span style={s.cardValue}>${bet.stake}</span>
                    </div>
                  </>
                )}

                {activeTab === 'PROMO' && (
                  <>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Promo Type</span>
                      <span style={{
                        ...s.badge,
                        ...(bet.promo_type === 'Mug Bet' ? s.badgeRed : bet.promo_type === 'Promo Bet' ? s.badgeBlue : s.badgeGreen),
                        fontSize: '11px'
                      }}>
                        {bet.promo_type}
                      </span>
                    </div>
                    <div style={s.betCardRow}>
                      <span style={s.cardLabel}>Bookmaker</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {BOOKMAKER_LOGOS[bet.bookmaker] && (
                          <img src={BOOKMAKER_LOGOS[bet.bookmaker]} alt={bet.bookmaker} style={{ width: '20px', height: '20px', borderRadius: '4px' }} />
                        )}
                        <span style={s.cardValue}>{bet.bookmaker}</span>
                      </div>
                    </div>
                    <div style={s.betCardRowLast}>
                      <span style={s.cardLabel}>Stake</span>
                      <span style={s.cardValue}>${bet.stake}</span>
                    </div>
                  </>
                )}

                <div style={s.actionButtons}>
                  {bet.status === 'pending' && activeTab === 'MIDDLE' && (
                    <>
                      <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="m-table-btn-won">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost (QL)" className="m-table-btn-lost" style={{ fontSize: '11px', fontWeight: '600' }}>QL</button>
                    </>
                  )}
                  {bet.status === 'pending' && isHorseBet(bet) && (
                    <>
                      <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="m-table-btn-won">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      {bet.bet_type_tag === 'horse_ev' && (
                        <button onClick={() => updateBetStatus(bet.id, 'placed')} title="Placed 2nd or 3rd (bonus back)" className="m-table-btn-lost" style={{ fontSize: '11px', fontWeight: '700', color: '#fbbf24' }}>P</button>
                      )}
                      <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost" className="m-table-btn-lost">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </button>
                    </>
                  )}
                  {bet.status === 'pending' && (activeTab === 'EV' || activeTab === 'ARB') && (
                    <>
                      <button onClick={() => updateBetStatus(bet.id, 'won')} title="Won" className="m-table-btn-won">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </button>
                      <button onClick={() => updateBetStatus(bet.id, 'lost')} title="Lost" className="m-table-btn-lost">
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </button>
                    </>
                  )}
                  <button onClick={() => editBet(bet.id)} title="Edit bet" className="m-table-btn-edit">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M9 1.5a1.5 1.5 0 012.12 2.12L4.5 10.25H2v-2.5L9 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M7.5 3l2 2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
                  </button>
                  <button onClick={() => deleteBet(bet.id)} title="Delete bet" className="m-table-btn-delete">
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M2 4h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/><path d="M5 4V2.5h3V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/><path d="M3 4l.7 7h5.6L10 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              </div>
              )
            })
          )}

          {/* Pagination */}
          {currentBets.length > itemsPerPage && (
            <div style={{ marginTop: '20px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '12px' }}>
                <button
                  onClick={() => goToPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  style={{ ...s.btn, backgroundColor: currentPage === 1 ? '#475569' : '#2563eb', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', padding: '6px 12px' }}
                >Prev</button>

                <div style={{ display: 'flex', gap: '4px' }}>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) pageNum = i + 1;
                    else if (currentPage <= 3) pageNum = i + 1;
                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                    else pageNum = currentPage - 2 + i;
                    return (
                      <button key={pageNum} onClick={() => goToPage(pageNum)} style={{ padding: '6px 10px', backgroundColor: currentPage === pageNum ? '#2563eb' : '#475569', color: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: currentPage === pageNum ? '600' : '400' }}>
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  style={{ ...s.btn, backgroundColor: currentPage === totalPages ? '#475569' : '#2563eb', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', padding: '6px 12px' }}
                >Next</button>
              </div>

              <select
                value={itemsPerPage}
                onChange={(e) => { const v = Number(e.target.value); setItemsPerPage(v); localStorage.setItem('tracker_items_per_page', v); setCurrentPage(1) }}
                style={{ ...s.input, fontSize: '12px', padding: '8px 10px', cursor: 'pointer', width: '100%' }}
              >
                <option value={25}>Show 25 per page</option>
                <option value={50}>Show 50 per page</option>
                <option value={100}>Show 100 per page</option>
              </select>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}